import { createFileRoute } from "@tanstack/react-router";
import { alternateLinkList } from "@/lib/i18n/locale";
import { BarChart3, LineChart, TrendingUp } from "lucide-react";
import { FeaturePage } from "@/components/marketing/FeaturePage";
import heroImg from "@/assets/analytics-hero.webp";
import cardsImg from "@/assets/analytics-cards.webp";
import teamImg from "@/assets/analytics-team.webp";

const IMG: Record<string, string> = { hero: heroImg, cards: cardsImg, team: teamImg };

export const Route = createFileRoute("/platform/analytics")({
  head: () => ({
    meta: [
      { title: "Analytics & Reporting — banana.cards" },
      { name: "description", content: "Real-time dashboards across cards, contacts, deals, contracts and bookings. Know exactly what's working." },
      { property: "og:title", content: "Analytics & Reporting — banana.cards" },
      { property: "og:description", content: "Real-time dashboards across cards, contacts, deals, contracts and bookings. Know exactly what's working." },
      { property: "og:image", content: IMG['hero'] },
    ],
    links: alternateLinkList("/platform/analytics", "en"),
  }),
  component: Page,
});

function Page() {
  return (
    <FeaturePage
      eyebrow="Analytics & Reporting"
      title="See what's working —"
      italic="down to every tap."
      description="Real-time dashboards across cards, contacts, deals, contracts and bookings. Know your top-performing card, top-performing rep, and top-performing event."
      heroImage={IMG['hero']}
      heroImageAlt="Analytics dashboard on banana.cards attributing card taps, leads and pipeline revenue by channel and event"
      metrics={[{ v: "Real-time", l: "data" }, { v: "30+", l: "reports" }, { v: "API", l: "exports" }]}
      bullets={[
        { icon: BarChart3, title: "Pre-built dashboards", desc: "Cards, leads, pipeline, contracts and bookings — out of the box." },
        { icon: TrendingUp, title: "Cohorts & funnels", desc: "Trace every lead from first tap to closed deal." },
        { icon: LineChart, title: "Custom reports", desc: "Build your own with filters, segments and saved views." }
      ]}
      deepDives={[
        { eyebrow: "Card analytics", title: "Every tap,", italic: "attributed.", description: "See which card design, channel and event drove which pipeline — by user, team and region.", imageAlt: "Per-card analytics report showing views, saves and leads by design, channel and event with UTM passthrough", image: IMG["cards"], reverse: false, points: ["Per-card views and saves", "Per-channel attribution", "Per-event lead tracking", "UTM passthrough"] },
        { eyebrow: "Team", title: "Coach with", italic: "data.", description: "Manager dashboards show velocity, response time, and where reps need help.", imageAlt: "Team performance dashboard with response-time SLAs, activity heatmaps and weekly digest emails", image: IMG["team"], reverse: true, points: ["Per-rep performance", "Response-time SLAs", "Activity heatmaps", "Weekly digest emails"] }
      ]}
      howItWorks={[
        { title: "Plug in your data", desc: "Banana captures everything natively." },
        { title: "Pick a dashboard", desc: "Or build a custom view." },
        { title: "Slice and segment", desc: "By team, region, channel or campaign." },
        { title: "Share & export", desc: "CSV, API and scheduled email digests." }
      ]}
      capabilities={[
        "Real-time dashboards",
        "Cohort & funnel analysis",
        "Custom reports & saved views",
        "Scheduled email digests",
        "CSV & API exports",
        "Per-team permissions",
        "UTM passthrough",
        "Manager coaching views"
      ]}
      faq={[
        { q: "What does Banana actually measure?", a: "Card views and unique visitors, lead conversions (taps that turned into a captured contact), top traffic sources (Direct, Instagram, LinkedIn, Google, WhatsApp, Email, UTM-tagged campaigns, etc.), country/region/city, device type, browser and OS, plus per-block engagement on your card (which links got clicked, which CTAs converted)." },
        { q: "Is the data real-time?", a: "Yes — views, taps and lead events stream into the dashboard as they happen. Charts refresh in seconds and you can switch time ranges (7 days, 30 days, 90 days, 1 year, all time) without re-loading the page." },
        { q: "Can I see analytics per card or per team member?", a: "Yes — drill into any individual card to see its views, sources and conversions, or roll up by team member to compare performance across sales reps, agents or stylists. Admins see the whole workspace; members see their own cards." },
        { q: "Can I track UTMs and tie views back to specific campaigns?", a: "Yes — any link to your card supports standard UTM parameters (source, medium, campaign, content, term). Banana captures and groups them so you can see exactly which Instagram ad, LinkedIn post or print QR campaign drove views and leads." },
        { q: "Can I export raw data or pipe it into GA4 / my warehouse?", a: "Yes — export any view as CSV, or pull events from the REST API on a schedule into Google Analytics 4, Mixpanel, PostHog, Snowflake or BigQuery. Webhooks fire on each event for real-time piping." },
        { q: "How is location detected, and is it accurate?", a: "Location is inferred from the visitor's IP at view time — accurate to city/region level for most cases, country-level otherwise. We never collect precise GPS coordinates and don't store IP addresses raw." }
      ]}
      outro={ { title: "Know what's working.", italic: "Do more of it.", description: "Real-time analytics across every Banana product." } }
    />
  );
}
